import React, { forwardRef } from "react";
import { Popover } from "antd";
import roundedQuestion from "assets/svg/rounded-question.svg";
import { ErrorIcon } from "assets/icons";

type GTextAreaProps = {
  label: string;
  placeholder: string;
  setInput: (val: string) => void;
  inputValue?: string;
  controlled?: boolean;
  hintText?: string;
  required?: boolean;
  error?: string;
  rows?: number;
  maxLength?: number;
  showCharCount?: boolean;
  labelClassName?: string;
  inputBackgroundColor?: string;
} & React.TextareaHTMLAttributes<HTMLTextAreaElement>;

export const GTextArea = forwardRef<HTMLTextAreaElement, GTextAreaProps>(
  (
    {
      label,
      placeholder,
      setInput,
      inputValue,
      controlled,
      hintText,
      required,
      error,
      rows = 4,
      maxLength,
      showCharCount = false,
      className,
      onChange,
      labelClassName,
      inputBackgroundColor,
      ...rest
    },
    ref,
  ) => {
    const isControlled = controlled || typeof inputValue === "string";
    const currentLength = inputValue?.length || 0;

    const handleChange: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
      onChange?.(e);
      setInput(e.target.value);
    };

    return (
      <div className={"my-3"}>
        <div className="flex items-center justify-between mb-2">
          <p
            className={
              labelClassName ??
              "text-[#344054] font-monaMedium relative text-xs sm:text-sm"
            }
          >
            {label}
            {required && (
              <span className="flex absolute -right-2 text-[#ED6E33] -top-1 items-center text-xl">
                *
              </span>
            )}
          </p>
          {hintText && (
            <Popover content={hintText}>
              <img className="cursor-pointer" src={roundedQuestion} alt="" />
            </Popover>
          )}
        </div>

        <textarea
          ref={ref}
          value={isControlled ? (inputValue ?? "") : undefined}
          onChange={handleChange}
          rows={rows}
          maxLength={maxLength}
          className={
            className ??
            `bg-[${inputBackgroundColor ?? "#F6F3EF"}] w-full px-4 py-3 rounded-lg border ${
              error
                ? "border-[#F04438] focus:border-[#F04438]"
                : "border-transparent focus:border-[#F6F3EF]"
            } font-sfRegular lg:text-sm tex t-[#101828] placeholder:text-[#667085] focus:outline-none resize-none transition-colors focus:border-transparent focus:ring-2 focus:ring-[#FF6929]`
          }
          placeholder={placeholder}
          {...rest}
        />

        {/* Character count */}
        {showCharCount && maxLength && (
          <div className="flex justify-end mt-1">
            <p
              className={`text-xs ${
                currentLength > maxLength * 0.9
                  ? "text-[#F04438]"
                  : "text-[#667085]"
              }`}
            >
              {currentLength}/{maxLength}
            </p>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="flex items-center gap-1.5 mt-1.5">
            <ErrorIcon className="mt-0.5 flex-shrink-0" />
            <p className="text-[#F04438] text-xs lg:text-sm font-sfRegular">
              {error}
            </p>
          </div>
        )}
      </div>
    );
  },
);

GTextArea.displayName = "GTextArea";
